import { Button } from "../components/Button";
import { Icon } from "../components/Icon";
import { cn } from "../lib/utils";
import { cva } from "class-variance-authority";
import React from "react";
const CONTENT_MESSAGE_VARIANTS = [
    "primary",
    "warning",
    "success",
    "highlight",
    "info",
    "green",
    "blue",
    "rose",
    "golden",
    "outline",
];
const CONTENT_MESSAGE_SIZES = ["sm", "md", "lg"];
const sharedVariantStyles = {
    primary: "bg-muted-background",
    success: "bg-success-100",
    warning: "bg-warning-100",
    highlight: "bg-highlight-100",
    info: "bg-info-100",
    green: "bg-success-100",
    blue: "bg-highlight-100",
    rose: "bg-warning-100",
    golden: "bg-info-100",
    outline: "bg-transparent border border-border",
};
const contentMessageVariants = cva("flex flex-col gap-1 rounded-2xl p-4 pl-5 min-h-[52px]", {
    variants: {
        variant: sharedVariantStyles,
        size: {
            lg: "",
            md: "max-w-xl",
            sm: "max-w-sm",
        },
    },
    defaultVariants: {
        variant: "info",
        size: "md",
    },
});
const contentMessageInlineVariants = cva("flex items-center gap-3 rounded-xl p-3 pl-4 min-h-[52px]", {
    variants: {
        variant: sharedVariantStyles,
    },
    defaultVariants: {
        variant: "info",
    },
});
const iconVariants = cva("shrink-0", {
    variants: {
        variant: {
            primary: "text-muted-foreground",
            warning: "text-warning-900",
            success: "text-success-900",
            highlight: "text-highlight-900",
            info: "text-info-900",
            green: "text-success-900",
            blue: "text-highlight-900",
            rose: "text-warning-900",
            golden: "text-info-900",
            outline: "text-muted-foreground",
        },
    },
});
const titleVariants = cva("heading-sm", {
    variants: {
        variant: {
            primary: "text-foreground",
            warning: "text-warning-900",
            success: "text-success-900",
            highlight: "text-highlight-900",
            info: "text-info-900",
            green: "text-success-900",
            blue: "text-highlight-900",
            rose: "text-warning-900",
            golden: "text-info-900",
            outline: "text-foreground",
        },
    },
});
const textVariants = cva("text-sm", {
    variants: {
        variant: {
            primary: "text-muted-foreground",
            warning: "text-warning-900",
            success: "text-success-900",
            highlight: "text-highlight-900",
            info: "text-info-900",
            green: "text-success-900",
            blue: "text-highlight-900",
            rose: "text-warning-900",
            golden: "text-info-900",
            outline: "text-muted-foreground",
        },
    },
});
function ContentMessage({ title, variant = "info", children, size = "md", className = "", icon, action, }) {
    return (React.createElement("div", { className: cn(contentMessageVariants({ variant, size }), className) },
        React.createElement("div", { className: cn("flex gap-3", action ? "items-center justify-between" : "flex-col") },
            React.createElement("div", { className: "flex flex-col gap-1" },
                (icon || title) && (React.createElement("div", { className: "flex items-center gap-1.5" },
                    icon && (React.createElement(Icon, { size: "sm", visual: icon, className: iconVariants({ variant }) })),
                    title && (React.createElement("div", { className: titleVariants({ variant }) }, title)))),
                children && (React.createElement("div", { className: textVariants({ variant }) }, children))),
            action && React.createElement("div", { className: "shrink-0" }, action))));
}
function ContentMessageAction(props) {
    return (React.createElement(Button, { size: "xs", className: cn("shrink-0", props.className), ...props }));
}
function ContentMessageInline({ title, variant = "info", children, className = "", icon, }) {
    const childrenArray = React.Children.toArray(children);
    const { actionChilds, contentChildren } = childrenArray.reduce(({ actionChilds, contentChildren }, child) => {
        if (React.isValidElement(child) && child.type === ContentMessageAction) {
            actionChilds.push(child);
        }
        else {
            contentChildren.push(child);
        }
        return { actionChilds, contentChildren };
    }, {
        actionChilds: [],
        contentChildren: [],
    });
    return (React.createElement("div", { className: cn(contentMessageInlineVariants({ variant }), className) },
        icon && (React.createElement(Icon, { size: "sm", visual: icon, className: iconVariants({ variant }) })),
        React.createElement("div", { className: cn("flex-1 min-w-0", textVariants({ variant })) },
            title && React.createElement("span", { className: titleVariants({ variant }) }, title),
            title && contentChildren.length > 0 && ": ",
            contentChildren),
        actionChilds));
}
export { ContentMessage, ContentMessageAction, ContentMessageInline };
//# sourceMappingURL=ContentMessage.js.map